import type { AxiosError, AxiosRequestConfig } from "axios";
import { SUPPORT_API_URL } from "@/environment";
import { useUserStore } from "@/stores/user.ts";

const AFFINITY_ERROR_TRACKER_ID_KEY = "affinity_error_tracker_id";

type AuthStateProvider = {
  isAuthenticated: () => boolean;
  getUserId: () => string | null;
};

type AffinityErrorReportPayload = {
  api: string;
  site: string;
  status: string | null;
  code: number | null;
  response_body: unknown;
  request_headers: Record<string, unknown>;
  request_body: unknown;
  is_authenticated: boolean;
  user_id: number | null;
  tracker_id: string | null;
  method: string;
  type?: string;
  user_sub: string | null;
};

let authStateProvider: AuthStateProvider = {
  isAuthenticated: () => false,
  getUserId: () => null,
};

export function configureAffinityErrorReporter(
  provider: Partial<AuthStateProvider>,
) {
  authStateProvider = {
    ...authStateProvider,
    ...provider,
  };
}

function generateTrackerId() {
  const uuid = globalThis.crypto?.randomUUID?.();

  if (uuid) {
    return uuid;
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateTrackerId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const existingTrackerId = window.localStorage.getItem(
    AFFINITY_ERROR_TRACKER_ID_KEY,
  );

  if (existingTrackerId) {
    return existingTrackerId;
  }

  const createdTrackerId = generateTrackerId();
  window.localStorage.setItem(AFFINITY_ERROR_TRACKER_ID_KEY, createdTrackerId);

  return createdTrackerId;
}

function getSiteRoute(): string {
  if (typeof window === "undefined") {
    return "";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getApiRoute(config?: AxiosRequestConfig): string {
  const requestUrl = config?.url || "";

  if (!requestUrl) {
    return "";
  }

  try {
    const base = config?.baseURL || window.location.origin;
    const url = new URL(requestUrl, base);
    return `${url.pathname}${url.search}`;
  } catch {
    return requestUrl;
  }
}

function toSerializable(value: unknown): unknown {
  if (typeof value === "undefined") {
    return null;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return String(value);
  }
}

function getRequestHeaders(
  config?: AxiosRequestConfig,
): Record<string, unknown> {
  const rawHeaders = config?.headers;

  if (!rawHeaders) {
    return {};
  }

  if (typeof (rawHeaders as { toJSON?: () => unknown }).toJSON === "function") {
    return toSerializable(
      (rawHeaders as { toJSON: () => unknown }).toJSON(),
    ) as Record<string, unknown>;
  }

  return toSerializable(rawHeaders) as Record<string, unknown>;
}

function getRequestBody(config?: AxiosRequestConfig): unknown {
  const method = (config?.method || "get").toUpperCase();

  if (method === "GET") {
    return toSerializable(config?.params);
  }

  return toSerializable(config?.data);
}

// Report Affinity API errors with optional type parameter
export async function reportAffinityApiError(
  error: AxiosError,
  type: string = "affinity",
) {
  const isUserAuthenticated = authStateProvider.isAuthenticated();

  let userId = null;
  let userSub = null;
  if (isUserAuthenticated) {
    const userStore = useUserStore();
    const profile = await userStore.getProfile(true);
    userId = profile?.id || null;
    userSub = profile?.auth0_sub || null;
  }

  const payload: AffinityErrorReportPayload = {
    api: getApiRoute(error.config),
    site: getSiteRoute(),
    code: error.response?.status || error.status || null,
    status: error.code || null,
    response_body: toSerializable(error.response?.data),
    request_headers: getRequestHeaders(error.config),
    request_body: getRequestBody(error.config),
    is_authenticated: isUserAuthenticated,
    user_id: userId,
    tracker_id: isUserAuthenticated ? null : getOrCreateTrackerId(),
    method: (error.config?.method || "get").toUpperCase(),
    type,
    user_sub: userSub,
  };

  try {
    await fetch(`${SUPPORT_API_URL}/v1/report/affinity`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Report failure must stay non-fatal for the user flow.
  }
}
